import { Button } from '@carbon/react';
import { FormattedCount } from 'components/formatted-count';
import type { ExclusionPopulations } from 'types/dps-capture';
import styles from './capture.module.scss';

interface ExclusionBannerProps {
  exclusions: ExclusionPopulations;
  /** Opts the area into Active Upside — only offered on the widenable line (AC 5). */
  onWiden?: () => void;
}

/**
 * The three-population exclusion banner (03-CAP §5). Three separately-counted
 * lines with the taxonomy vocabulary: "no active type curve", "excluded (Active
 * Upside)" — the one the user can widen — and "will not be valued" (never "$0").
 * Only the Active-Upside line carries the "Widen" affordance.
 *
 * Functional layout extending the v4 single-state banner; final visual is
 * UX-owed. Suppressed entirely when nothing is excluded.
 *
 * @param {ExclusionBannerProps} props - The three counts and an optional widen handler.
 * @return {JSX.Element | null} The banner, or null when there is nothing to show.
 */
export function ExclusionBanner({
  exclusions,
  onWiden,
}: ExclusionBannerProps): JSX.Element | null {
  const total =
    exclusions.noActiveTypeCurve +
    exclusions.upsideExcluded +
    exclusions.noForecastSource;
  if (total === 0) return null;

  return (
    <div className={styles.banner} role="status">
      <div className={styles.bannerLine}>
        <span className={styles.bannerCount}>
          <FormattedCount value={exclusions.noActiveTypeCurve} />
        </span>
        <span>locations have no active type curve — not scheduled</span>
      </div>
      <div className={styles.bannerLine}>
        <span className={styles.bannerCount}>
          <FormattedCount value={exclusions.upsideExcluded} />
        </span>
        <span>Active Upside locations excluded from this area</span>
        {onWiden && (
          <Button kind="ghost" size="sm" onClick={onWiden}>
            Widen
          </Button>
        )}
      </div>
      <div className={styles.bannerLine}>
        <span className={styles.bannerCount}>
          <FormattedCount value={exclusions.noForecastSource} />
        </span>
        <span>scheduled locations will not be valued (no forecast source)</span>
      </div>
    </div>
  );
}
